import React, { useState, useEffect } from "react";
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Grid,
  useTheme,
  IconButton,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  FormControl,
  Select,
  MenuItem,
} from "@mui/material";
import { motion } from "framer-motion";
import { Helmet } from "react-helmet-async";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import HourglassTopIcon from "@mui/icons-material/HourglassTop";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import RefreshIcon from "@mui/icons-material/Refresh";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import CancelIcon from "@mui/icons-material/Cancel";
import ReceiptIcon from "@mui/icons-material/Receipt";
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import AssessmentIcon from '@mui/icons-material/Assessment';
import axios from "../../config/axios";

const Finance = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showProfit, setShowProfit] = useState(false);
  const theme = useTheme();

  const currentYear = new Date().getFullYear();
  const startYear = 2025;
  const availableYears = [];
  for (let y = startYear; y <= Math.max(startYear, currentYear); y++) {
    availableYears.push(y);
  }
  const [selectedYear, setSelectedYear] = useState(currentYear);

  const fetchFinanceData = async (year) => {
    const yearToFetch = year !== undefined ? year : selectedYear;
    setLoading(true);
    try {
      const response = await axios.get("/orders_amount_summary.php", {
        params: { year: yearToFetch },
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });
      if (response.data.success) {
        setData(response.data.data);
      } else {
        setError(response.data.message || "Failed to load finance data");
      }
    } catch (err) {
      setError("Failed to load finance data");
    } finally {
      setLoading(false);
    }
  };

  const handleYearChange = (event) => {
    const year = event.target.value;
    setSelectedYear(year);
    fetchFinanceData(year);
  };

  useEffect(() => {
    fetchFinanceData(currentYear);
  }, []);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut",
      },
    },
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "60vh",
          flexDirection: "column",
          gap: 2,
        }}
      >
        <CircularProgress size={60} sx={{ color: "primary.main" }} />
        <Typography variant="body1" sx={{ color: "text.secondary" }}>
          Loading financial data...
        </Typography>
      </Box>
    );
  }

  const financialCards = [
    {
      title: "Total Revenue",
      value: `₹ ${Math.round(data?.total_order_amount || 0).toLocaleString()}`,
      icon: <AttachMoneyIcon />,
      color: "linear-gradient(135deg, #B51D6E 0%, #D81B60 100%)",
      subtitle: "Overall earnings",
    },
    {
      title: "Amount Received",
      value: `₹ ${Math.round(data?.total_advance_amount || 0).toLocaleString()}`,
      icon: <AccountBalanceIcon />,
      color: "linear-gradient(102.3deg, rgba(147,39,143,1) 5.9%, rgba(234,172,232,1) 64%, rgba(246,219,245,1) 89%)",
      subtitle: "Deposited amount",
    },
    {
      title: "Pending Amount",
      value: `₹ ${Math.round(data?.total_pending_amount || 0).toLocaleString()}`,
      icon: <HourglassTopIcon />,
      color: "linear-gradient(135deg, #FF9800 0%, #F57C00 100%)",
      subtitle: "Awaiting payment",
    },
    {
      title: "Estimated Profit",
      value: `₹ ${Math.round((data?.total_profit_received || 0) - (data?.total_expense || 0) - 155421 - (data?.total_cancelled_loss || 0) + (data?.total_pending_amount || 0)).toLocaleString()}`,
      icon: <AssessmentIcon />,
      color: "linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)",
      subtitle: "Projected total profit",
    },
  ];

  const ALL_STATUSES = [
    "pending-approval",
    "accepted",
    "in-progress",
    "ready-to-ship",
    "dispatched",
    "delivered",
    "completed",
    "canceled"
  ];

  const getStatusSummary = () => {
    const summary = {};
    ALL_STATUSES.forEach(status => {
      summary[status] = data?.status_summary?.[status] || {
        count: 0,
        total_amount: 0,
        advance_amount: 0,
        pending_amount: 0
      };
    });
    return Object.entries(summary);
  };

  return (
    <Box sx={{ background: "#FAF9F6", minHeight: "100vh", py: 4 }}>
      <Helmet>
        <title>Finance - Ishqnama Art</title>
      </Helmet>

      <Container maxWidth="xl">
        <Box sx={{ mb: 4, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 900, color: "text.primary", fontFamily: "'Playfair Display', serif" }}>
              Financial Insights
            </Typography>
            <Typography variant="body1" sx={{ color: "text.secondary" }}>
              Detailed breakdown of revenue and order statuses
            </Typography>
          </Box>
          <Tooltip title="Refresh Data">
            <IconButton onClick={fetchFinanceData} sx={{ bgcolor: "white", boxShadow: 1 }}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>

        {error && <Alert severity="error" sx={{ mb: 4, borderRadius: 2 }}>{error}</Alert>}

        <motion.div variants={containerVariants} initial="hidden" animate="visible">
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 1.5,
              mb: 6,
              "& > *": {
                flex: { xs: "1 1 calc(50% - 12px)", sm: "1 1 calc(25% - 12px)" },
              }
            }}
          >
            {financialCards.map((card, index) => (
              <motion.div key={index} variants={cardVariants} style={{ display: 'flex' }}>
                <Card
                  sx={{
                    width: "100%",
                    borderRadius: 1.5,
                    boxShadow: "0 4px 20px rgba(0,0,0,0.05)",
                    border: "1px solid rgba(0,0,0,0.05)",
                    position: "relative",
                    overflow: "hidden",
                    display: 'flex',
                    flexDirection: 'column'
                  }}
                >
                  <CardContent sx={{ p: { xs: 1.5, md: 2.5 }, textAlign: "center", flexGrow: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                    <Box
                      sx={{
                        width: { xs: 36, md: 44 },
                        height: { xs: 36, md: 44 },
                        borderRadius: 1,
                        background: card.color,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: "white",
                        mx: "auto",
                        mb: 1.5,
                        boxShadow: 2,
                      }}
                    >
                      {React.cloneElement(card.icon, { sx: { fontSize: { xs: 16, md: 20 } } })}
                    </Box>
                    <Typography variant="h5" sx={{ fontWeight: 900, mb: 0.25, fontSize: { xs: "1rem", md: "1.25rem" }, display: "flex", alignItems: "center", justifyContent: "center" }}>
                      {card.value}
                    </Typography>
                    <Typography variant="body2" sx={{ color: "text.secondary", fontWeight: 700, textTransform: "uppercase", fontSize: { xs: "0.5rem", md: "0.6rem" }, letterSpacing: "0.08em" }}>
                      {card.title}
                    </Typography>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </Box>

          <Typography variant="h5" sx={{ fontWeight: 800, mb: 3, fontFamily: "'Playfair Display', serif", color: "text.primary" }}>
            Profit Breakdown
          </Typography>

          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 1.5,
              mb: 6,
              "& > *": {
                flex: { xs: "1 1 calc(50% - 12px)", sm: "1 1 calc(33.33% - 12px)", lg: "1 1 calc(20% - 12px)" },
              }
            }}
          >
            {[
              { title: "Amount Received", value: `₹ ${Math.round(data?.total_profit_received || 0).toLocaleString()}`, icon: <AccountBalanceIcon />, color: "linear-gradient(135deg, #10B981 0%, #059669 100%)" },
              { title: "Amount Expense (Oct 25 - Present)", value: `₹ ${Math.round(data?.total_expense || 0).toLocaleString()}`, icon: <ReceiptIcon />, color: "linear-gradient(135deg, #EF4444 0%, #DC2626 100%)" },
              { title: "Amount Expense (Jul 25 - Sep 25)", value: "₹ 1,55,421", icon: <ReceiptIcon />, color: "linear-gradient(135deg, #EF4444 0%, #DC2626 100%)" },
              { title: "Amount Loss (Cancelled)", value: `₹ ${Math.round(data?.total_cancelled_loss || 0).toLocaleString()}`, icon: <CancelIcon />, color: "linear-gradient(135deg, #6B7280 0%, #4B5563 100%)" },
              { title: "Business Profit", value: `₹ ${Math.round((data?.total_profit_received || 0) - (data?.total_expense || 0) - 155421 - (data?.total_cancelled_loss || 0)).toLocaleString()}`, icon: <TrendingUpIcon />, color: "linear-gradient(135deg, #10B981 0%, #059669 100%)" }
            ].map((card, idx) => (
              <motion.div key={idx} variants={cardVariants} style={{ display: 'flex' }}>
                <Card sx={{ width: "100%", borderRadius: 1.5, boxShadow: "0 4px 20px rgba(0,0,0,0.05)", border: "1px solid rgba(0,0,0,0.05)", position: "relative", overflow: "hidden" }}>
                  <CardContent sx={{ p: { xs: 1.5, md: 2.5 }, textAlign: "center", width: '100%' }}>
                    <Box sx={{ width: { xs: 36, md: 44 }, height: { xs: 36, md: 44 }, borderRadius: 1, background: card.color, display: "flex", alignItems: "center", justifyContent: "center", color: "white", mx: "auto", mb: 1.5, boxShadow: 2 }}>
                      {React.cloneElement(card.icon, { sx: { fontSize: { xs: 16, md: 20 } } })}
                    </Box>
                    <Typography variant="h5" sx={{ fontWeight: 900, mb: 0.25, fontSize: { xs: "1rem", md: "1.25rem" } }}>
                      {card.value}
                    </Typography>
                    <Typography variant="body2" sx={{ color: "text.secondary", fontWeight: 700, textTransform: "uppercase", fontSize: { xs: "0.5rem", md: "0.6rem" }, letterSpacing: "0.08em" }}>
                      {card.title}
                    </Typography>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </Box>

          <Box sx={{ mb: 3, display: "flex", flexWrap: "wrap", alignItems: "baseline", gap: { xs: 1, md: 2 } }}>
            <Typography variant="h5" sx={{ fontWeight: 800, fontFamily: "'Playfair Display', serif", color: "text.primary" }}>
              Income & Order Stats
            </Typography>
            {(() => {
              const start = new Date("2025-07-16");
              const end = new Date();
              let months = (end.getFullYear() - start.getFullYear()) * 12 + (end.getMonth() - start.getMonth());
              let days = end.getDate() - start.getDate();
              if (days < 0) {
                months--;
                const prevMonth = new Date(end.getFullYear(), end.getMonth(), 0);
                days += prevMonth.getDate();
              }
              return (
                <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 600, fontSize: { xs: "0.65rem", md: "0.75rem" } }}>
                  ({months} Months, {days} Days)
                </Typography>
              );
            })()}
          </Box>

          {(() => {
            const startDate = new Date("2025-07-16");
            const today = new Date();
            const daysElapsed = Math.max(1, Math.ceil(Math.abs(today - startDate) / (1000 * 60 * 60 * 24)));
            const totalReceived = data?.total_profit_received || 0;
            const businessProfit = totalReceived - (data?.total_expense || 0) - 155421 - (data?.total_cancelled_loss || 0);
            const totalOrders = Object.values(data?.status_summary || {}).reduce((sum, s) => sum + s.count, 0);

            const stats = [
              { title: "Profit / Day", value: `₹ ${Math.round(businessProfit / daysElapsed).toLocaleString()}`, color: "#10B981" },
              { title: "Profit / Week", value: `₹ ${Math.round((businessProfit / daysElapsed) * 7).toLocaleString()}`, color: "#10B981" },
              { title: "Profit / Month", value: `₹ ${Math.round((businessProfit / daysElapsed) * 30).toLocaleString()}`, color: "#10B981" },
              { title: "Total Orders", value: totalOrders.toLocaleString(), color: "#3b82f6" },
              { title: "Orders / Day", value: (totalOrders / daysElapsed).toFixed(2), color: "#3b82f6" },
              { title: "Orders / Week", value: ((totalOrders / daysElapsed) * 7).toFixed(1), color: "#3b82f6" },
              { title: "Orders / Month", value: Math.round((totalOrders / daysElapsed) * 30).toLocaleString(), color: "#3b82f6" },
            ];

            return (
              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 1.5,
                  mb: 6,
                  "& > *": {
                    flex: { xs: "1 1 calc(50% - 12px)", sm: "1 1 calc(33.33% - 12px)", md: "1 1 calc(25% - 12px)", lg: "1 1 calc(14.28% - 12px)" },
                  }
                }}
              >
                {stats.map((stat, idx) => (
                  <motion.div key={idx} variants={cardVariants} style={{ display: 'flex' }}>
                    <Card sx={{ width: "100%", borderRadius: 1.5, border: "1px solid rgba(0,0,0,0.05)", boxShadow: "0 2px 10px rgba(0,0,0,0.02)" }}>
                      <CardContent sx={{ p: { xs: 1.5, md: 2 }, textAlign: "center" }}>
                        <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 800, textTransform: "uppercase", fontSize: { xs: "0.5rem", md: "0.55rem" }, display: "block", mb: 1, letterSpacing: "0.05em" }}>
                          {stat.title}
                        </Typography>
                        <Typography variant="h6" sx={{ fontWeight: 900, color: stat.color, fontSize: { xs: "0.85rem", md: "1.1rem" } }}>
                          {stat.value}
                        </Typography>
                      </CardContent>
                    </Card>
                  </motion.div>
                ))}
              </Box>
            );
          })()}

          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 3,
              flexWrap: "wrap",
              gap: 2,
            }}
          >
            <Typography
              variant="h5"
              sx={{
                fontWeight: 800,
                fontFamily: "'Playfair Display', serif",
                color: "text.primary",
              }}
            >
              Monthly Financial Performance
            </Typography>
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <Select
                value={selectedYear}
                onChange={handleYearChange}
                sx={{
                  borderRadius: 2,
                  bgcolor: "background.paper",
                  fontWeight: 700,
                  fontSize: "0.875rem",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: "rgba(0,0,0,0.12)",
                  },
                }}
              >
                {availableYears.map((year) => (
                  <MenuItem key={year} value={year} sx={{ fontWeight: 600 }}>
                    Year {year}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          <Box sx={{ mb: 6 }}>
            <Card sx={{ borderRadius: 3, border: "1px solid rgba(0,0,0,0.06)", boxShadow: "0 10px 30px rgba(0,0,0,0.02)", overflow: 'hidden' }}>
              <TableContainer>
                <Table sx={{ minWidth: 650 }}>
                  <TableHead sx={{ bgcolor: 'rgba(0,0,0,0.02)' }}>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 800, color: 'text.secondary', fontSize: '0.75rem', textTransform: 'uppercase' }}>Month</TableCell>
                      <TableCell sx={{ fontWeight: 800, color: 'text.secondary', fontSize: '0.75rem', textTransform: 'uppercase' }}>Revenue</TableCell>
                      <TableCell sx={{ fontWeight: 800, color: 'text.secondary', fontSize: '0.75rem', textTransform: 'uppercase' }}>Received</TableCell>
                      <TableCell sx={{ fontWeight: 800, color: 'text.secondary', fontSize: '0.75rem', textTransform: 'uppercase' }}>Pending</TableCell>
                      <TableCell sx={{ fontWeight: 800, color: 'text.secondary', fontSize: '0.75rem', textTransform: 'uppercase' }}>Expense</TableCell>
                      <TableCell sx={{ fontWeight: 800, color: 'text.secondary', fontSize: '0.75rem', textTransform: 'uppercase' }}>Loss (Cancl)</TableCell>
                      <TableCell sx={{ fontWeight: 800, color: 'text.secondary', fontSize: '0.75rem', textTransform: 'uppercase' }}>Net Profit</TableCell>
                      <TableCell sx={{ fontWeight: 800, color: 'text.secondary', fontSize: '0.75rem', textTransform: 'uppercase' }}>Est. Profit</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {data?.monthly_breakdown
                      ?.filter((m) => m.revenue !== 0)
                      ?.map((m, idx) => (
                        <TableRow key={idx} sx={{ '&:hover': { bgcolor: 'rgba(0,0,0,0.01)' } }}>
                          <TableCell sx={{ fontWeight: 700 }}>{m.month}</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>₹{Math.round(m.revenue).toLocaleString()}</TableCell>
                          <TableCell sx={{ fontWeight: 600, color: '#10B981' }}>₹{Math.round(m.received).toLocaleString()}</TableCell>
                          <TableCell sx={{ fontWeight: 600, color: '#FF9800' }}>₹{Math.round(m.pending).toLocaleString()}</TableCell>
                          <TableCell sx={{ fontWeight: 600, color: '#EF4444' }}>₹{Math.round(m.expense).toLocaleString()}</TableCell>
                          <TableCell sx={{ fontWeight: 600, color: 'text.secondary' }}>₹{Math.round(m.loss).toLocaleString()}</TableCell>
                          <TableCell sx={{ fontWeight: 900, color: m.business_profit >= 0 ? '#10B981' : '#EF4444' }}>
                            ₹{Math.round(m.business_profit).toLocaleString()}
                          </TableCell>
                          <TableCell sx={{ fontWeight: 900, color: '#3b82f6' }}>
                            ₹{Math.round(m.estimated_profit).toLocaleString()}
                          </TableCell>
                        </TableRow>
                      ))}
                    {(!data?.monthly_breakdown?.length ||
                      data?.monthly_breakdown?.filter((m) => m.revenue !== 0).length === 0) && (
                        <TableRow>
                          <TableCell colSpan={8} sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
                            No monthly data available for the selected year.
                          </TableCell>
                        </TableRow>
                      )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Card>
          </Box>

          <Typography variant="h5" sx={{ fontWeight: 800, mb: 3, fontFamily: "'Playfair Display', serif", color: "text.primary" }}>
            Status Breakdown
          </Typography>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "repeat(2, 1fr)",
                sm: "repeat(2, 1fr)",
                md: "repeat(4, 1fr)",
              },
              gap: 1.5,
            }}
          >
            {getStatusSummary().map(([status, summary], index) => {
              const statusLower = status.toLowerCase();
              let Icon = ShoppingCartIcon;
              let color = "#B51D6E";

              if (statusLower.includes("pending")) {
                Icon = HourglassTopIcon;
                color = "#FF9800";
              } else if (statusLower.includes("accepted") || statusLower.includes("progress")) {
                Icon = TrendingUpIcon;
                color = "#3b82f6";
              } else if (statusLower.includes("ship") || statusLower.includes("dispatch")) {
                Icon = LocalShippingIcon;
                color = "#8b5cf6";
              } else if (statusLower.includes("delivered") || statusLower.includes("complete")) {
                Icon = AccountBalanceIcon;
                color = "#10B981";
              } else if (statusLower.includes("cancel")) {
                Icon = CancelIcon;
                color = "#ef4444";
              }

              return (
                <motion.div key={status} variants={cardVariants}>
                  <Card
                    sx={{
                      height: "100%",
                      borderRadius: 1.5,
                      border: "1px solid rgba(0,0,0,0.05)",
                      boxShadow: "0 4px 15px rgba(0,0,0,0.03)",
                      position: "relative",
                      overflow: "hidden",
                      background: "white",
                      "&:hover": {
                        boxShadow: "0 8px 25px rgba(0,0,0,0.06)",
                        "& .status-icon-bg": { transform: "scale(1.1) rotate(-10deg)" }
                      }
                    }}
                  >
                    <Icon sx={{
                      position: "absolute",
                      right: -10,
                      top: -10,
                      fontSize: 60,
                      opacity: 0.03,
                      color: color
                    }} />

                    <CardContent sx={{ p: 2 }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                        <Box
                          className="status-icon-bg"
                          sx={{
                            width: 34,
                            height: 34,
                            borderRadius: 1,
                            bgcolor: `${color}15`,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: color,
                            transition: "all 0.3s ease"
                          }}
                        >
                          <Icon sx={{ fontSize: 18 }} />
                        </Box>
                        <Box>
                          <Typography variant="subtitle2" sx={{ textTransform: "capitalize", fontWeight: 800, color: "text.primary", lineHeight: 1, fontSize: "0.8rem" }}>
                            {status.replace(/-/g, " ")}
                          </Typography>
                          <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 600, fontSize: "0.65rem" }}>
                            {summary.count} {summary.count === 1 ? 'Order' : 'Orders'}
                          </Typography>
                        </Box>
                      </Box>

                      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                          <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 600 }}>Total Value</Typography>
                          <Typography variant="caption" sx={{ fontWeight: 800, color: "text.primary" }}>
                            ₹ {Math.round(summary.total_amount).toLocaleString()}
                          </Typography>
                        </Box>

                        <Box sx={{ p: 1, bgcolor: "#F8F9FA", borderRadius: 1, display: "flex", flexDirection: "column", gap: 0.75 }}>
                          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                            <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 700, fontSize: "0.6rem" }}>RECEIVED</Typography>
                            <Typography variant="caption" sx={{ fontWeight: 900, color: "#10B981", fontSize: "0.7rem" }}>
                              ₹ {Math.round(summary.advance_amount).toLocaleString()}
                            </Typography>
                          </Box>
                          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                            <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 700, fontSize: "0.6rem" }}>PENDING</Typography>
                            <Typography variant="caption" sx={{ fontWeight: 900, color: "#FF9800", fontSize: "0.7rem" }}>
                              ₹ {Math.round(summary.pending_amount).toLocaleString()}
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </motion.div>
              );
            })}
          </Box>
        </motion.div>
      </Container>
    </Box>
  );
};

export default Finance;
